<template>
  <transition>
    <div class="main-loader" v-if="loading">
      <icon-component iconName="loader" />
    </div>
  </transition>

  <the-alert></the-alert>
  <mobile-print-agent v-if="!isAuthRoute" @windows-printer="ensureOrderingPrintService" />

  <div
    ref="appViewport"
    class="app-viewport"
    :class="{ 'app-viewport--scaled': tabletLayoutActive }"
    :style="appViewportStyle"
  >
    <div
      class="app-canvas"
      :style="appCanvasStyle"
    >
      <header-component
        v-if="!isAuthRoute && !guestFlowActive"
      ></header-component>

      <div
        class="wrapper"
        :class="{ 'guest-flow-wrapper': guestFlowActive }"
      >
        <sidebar-component
          v-if="!isAuthRoute && !guestFlowActive"
        ></sidebar-component>

        <main id="content">
          <router-view></router-view>
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import MobilePrintAgent from '@/components/printers/MobilePrintAgent.vue';
import SidebarComponent from "@/components/_global/Sidebar";
import HeaderComponent from "@/components/_global/Header";
import TheAlert from "@/components/_global/TheAlert";
import { printingCheckLoop } from "@/_helpers/printing-check-loop";
import { businessDayService } from "@/_helpers/business-day-service";
import store from "@/store";
import iconComponent from "@/components/_shared/Icon.vue";
import { settingsService } from "@/_api/settings.api";
import { printersService } from "@/_api/printers.api";
import * as JSPM from "jsprintmanager";
import { webusbPrinter } from "@/_helpers/webusb-printer";

const TABLET_MIN_WIDTH = 600;
const TABLET_MAX_WIDTH = 1440;
const TABLET_CANVAS_MIN_WIDTH = 1280;

export default {
  name: "AppRoot",

  components: {
    TheAlert,
    MobilePrintAgent,
    HeaderComponent,
    SidebarComponent,
    iconComponent,
  },

  data() {
    return {
      loading: true,
      tabletLayoutActive: false,
      tabletScale: 1,
      tabletViewportWidth: 0,
      tabletViewportHeight: 0,
      tabletCanvasWidth: TABLET_CANVAS_MIN_WIDTH,
      tabletCanvasHeight: 0,
      tabletLayoutFrame: 0,
      tabletLayoutTimer: 0,
      tabletResizeObserver: null,
    };
  },

  computed: {
    guestFlowActive() {
      return this.$store.state.settings.guestFlowActive;
    },

    design() {
      return this.$store.state.UI;
    },

    isAuthRoute() {
      // using this param to completely delete from dom sidebar when user is unauthorized
      return this.$route.path.includes("auth");
    },

    authData() {
      return this.$store.state.auth;
    },

    hasCheckPrinter() {
      return (
        this.$store.state.settings?.printers?.filter(
          (printer) => printer.connection_type == 3
        ) || []
      );
    },

    hasWebUsbPrinter() {
      return (
        this.$store.state.settings?.printers?.filter(
          (printer) => printer.connection_type == 4
        ) || []
      );
    },

    appViewportStyle() {
      if (!this.tabletLayoutActive) {
        return {};
      }

      return {
        width: `${this.tabletViewportWidth}px`,
        height: `${this.tabletViewportHeight}px`,
      };
    },

    appCanvasStyle() {
      if (!this.tabletLayoutActive) {
        return {};
      }

      return {
        width: `${this.tabletCanvasWidth}px`,
        height: `${this.tabletCanvasHeight}px`,
        transform: `scale(${this.tabletScale})`,
        "--app-width": `${this.tabletCanvasWidth}px`,
        "--app-height": `${this.tabletCanvasHeight}px`,
        "--app-scale": String(this.tabletScale),
      };
    },
  },

  created() {
    // businessDayService.checkBusinessDay();
    setTimeout(() => {
      this.loading = false;
      document.body.classList.remove("loading");
      this.$nextTick(this.scheduleTabletLayout);
    }, 1000);
  },

  mounted() {
    this.scheduleTabletLayout();

    window.addEventListener(
      "resize",
      this.scheduleTabletLayout
    );

    window.addEventListener(
      "orientationchange",
      this.scheduleTabletLayout
    );

    window.addEventListener(
      "pageshow",
      this.scheduleTabletLayout
    );

    window.visualViewport?.addEventListener(
      "resize",
      this.scheduleTabletLayout
    );

    if (window.ResizeObserver) {
      this.tabletResizeObserver = new ResizeObserver(
        this.scheduleTabletLayout
      );

      this.tabletResizeObserver.observe(
        document.documentElement
      );
    }

    if (
      store.state.PRINT_SERVICE_API_BASE.length &&
      store.state.auth.posId
    ) {
      // printingCheckLoop();
    }

    // console.log('*****88',this.authData.status.loggedIn);
    // if (this.authData.status.loggedIn) {
    //  settingsService.getLastBusinessDay().then((res) => {
    //    store.dispatch('setBusinessDay', res)
    //  })
    // }
  },

  beforeUnmount() {
    window.removeEventListener(
      "resize",
      this.scheduleTabletLayout
    );

    window.removeEventListener(
      "orientationchange",
      this.scheduleTabletLayout
    );

    window.removeEventListener(
      "pageshow",
      this.scheduleTabletLayout
    );

    window.visualViewport?.removeEventListener(
      "resize",
      this.scheduleTabletLayout
    );

    this.tabletResizeObserver?.disconnect();

    if (this.tabletLayoutFrame) {
      cancelAnimationFrame(
        this.tabletLayoutFrame
      );
    }

    if (this.tabletLayoutTimer) {
      clearTimeout(
        this.tabletLayoutTimer
      );
    }

    this.resetTabletLayout();

    // businessDayService.stopChecking();
  },

  methods: {
    scheduleTabletLayout() {
      if (this.tabletLayoutFrame) {
        cancelAnimationFrame(
          this.tabletLayoutFrame
        );
      }

      if (this.tabletLayoutTimer) {
        clearTimeout(
          this.tabletLayoutTimer
        );
      }

      this.tabletLayoutFrame = requestAnimationFrame(() => {
        this.tabletLayoutFrame = requestAnimationFrame(() => {
          this.tabletLayoutFrame = 0;
          this.updateTabletLayout();
        });
      });

      this.tabletLayoutTimer = window.setTimeout(() => {
        this.tabletLayoutTimer = 0;
        this.updateTabletLayout();
      }, 250);
    },

    updateTabletLayout() {
      const viewportWidth = Math.round(
        document.documentElement.clientWidth ||
        window.innerWidth
      );

      const viewportHeight = Math.round(
        document.documentElement.clientHeight ||
        window.innerHeight
      );

      if (!viewportWidth || !viewportHeight) {
        return;
      }

      const isTouchCapable =
        navigator.maxTouchPoints > 0 ||
        window.matchMedia("(pointer: coarse)").matches ||
        window.matchMedia("(any-pointer: coarse)").matches ||
        /Android|iPad|iPhone|iPod/i.test(
          navigator.userAgent
        ) ||
        (
          navigator.platform === "MacIntel" &&
          navigator.maxTouchPoints > 1
        );

      // All terminal pages share one canvas scale, including POS.
      const shouldScale =
        isTouchCapable &&
        viewportWidth >= TABLET_MIN_WIDTH &&
        viewportWidth <= TABLET_MAX_WIDTH &&
        !this.isAuthRoute &&
        !this.guestFlowActive;

      if (!shouldScale) {
        this.resetTabletLayout();
        return;
      }

      const canvasWidth = Math.max(
        TABLET_CANVAS_MIN_WIDTH,
        viewportWidth
      );

      const scale = Math.min(
        1,
        viewportWidth / canvasWidth
      );

      const canvasHeight = Math.ceil(
        viewportHeight / scale
      );

      this.tabletViewportWidth = viewportWidth;
      this.tabletViewportHeight = viewportHeight;
      this.tabletCanvasWidth = canvasWidth;
      this.tabletCanvasHeight = canvasHeight;
      this.tabletScale = scale;
      this.tabletLayoutActive = true;
      document.body.style.setProperty('--terminal-scale', String(scale));
      document.body.style.setProperty('--terminal-canvas-width', `${canvasWidth}px`);
      document.body.style.setProperty('--terminal-canvas-height', `${canvasHeight}px`);

      document.documentElement.classList.add(
        "tablet-layout-active"
      );

      document.body.classList.add(
        "tablet-layout-active"
      );
    },

    resetTabletLayout() {
      this.tabletLayoutActive = false;
      this.tabletScale = 1;
      this.tabletViewportWidth = 0;
      this.tabletViewportHeight = 0;
      this.tabletCanvasWidth = TABLET_CANVAS_MIN_WIDTH;
      this.tabletCanvasHeight = 0;
      document.body.style.removeProperty('--terminal-scale');
      document.body.style.removeProperty('--terminal-canvas-width');
      document.body.style.removeProperty('--terminal-canvas-height');

      document.documentElement.classList.remove(
        "tablet-layout-active"
      );

      document.body.classList.remove(
        "tablet-layout-active"
      );
    },

    ensureOrderingPrintService() {
      if (!this.hasCheckPrinter.length) this.startWebPrintService();
    },

    startWebPrintService() {
      JSPM.JSPrintManager.license_url =
        store.state.API_BASE + "/web-print/license";

      JSPM.JSPrintManager.auto_reconnect = false;

      this._discoverAndConnect();
    },

    _discoverAndConnect() {
      JSPM.JSPrintManager.getInstances()
        .then((data) => {
          let instances = [];

          try {
            instances =
              JSON.parse(data)?.instances ?? [];
          } catch {
            console.warn(
              "JSPM: failed to parse getInstances response"
            );
          }

          if (
            !instances ||
            (
              Array.isArray(instances) &&
              !instances.length
            )
          ) {
            console.warn(
              "JSPM: no instance found, retrying in 3s..."
            );

            setTimeout(() => {
              this._discoverAndConnect();
            }, 3000);

            return;
          }

          const targetInstance = Array.isArray(instances)
            ? instances[0]
            : instances;

          const { port } = targetInstance;

          JSPM.JSPrintManager.port = port;

          JSPM.JSPrintManager.stop();
          JSPM.JSPrintManager.start();

          JSPM.JSPrintManager.WS.onStatusChanged = () => {
            if (
              JSPM.JSPrintManager.websocket_status ===
              JSPM.WSStatus.Closed
            ) {
              console.warn(
                "JSPM: connection lost, restarting port discovery..."
              );

              this.stopWebPrintService();

              setTimeout(() => {
                this._discoverAndConnect();
              }, 3000);
            }
          };
        })
        .catch(() => {
          console.warn(
            "JSPM: client app unreachable, retrying in 3s..."
          );

          setTimeout(() => {
            this._discoverAndConnect();
          }, 3000);
        });
    },

    stopWebPrintService() {
      if (JSPM.JSPrintManager.WS) {
        JSPM.JSPrintManager.WS.onStatusChanged =
          null;
      }

      JSPM.JSPrintManager.stop();
    },
  },

  watch: {
    "$route.path"() {
      this.$nextTick(
        this.scheduleTabletLayout
      );
    },

    guestFlowActive() {
      this.$nextTick(
        this.scheduleTabletLayout
      );
    },

    hasCheckPrinter: {
      handler(newValue, oldValue) {
        if (newValue.length) {
          this.startWebPrintService();
        }

        if (
          oldValue.length == 1 &&
          newValue.length == 0
        ) {
          this.stopWebPrintService();
          window.location.reload();
        }
      },

      deep: true,
    },

    hasWebUsbPrinter: {
      handler(newValue) {
        if (
          newValue.length &&
          "usb" in navigator
        ) {
          const serial =
            newValue[0].os_printer_name;

          if (serial) {
            webusbPrinter.silentConnect(serial);
          }
        }
      },

      deep: true,
      immediate: true,
    },
  },
};
</script>

<style>
.guest-flow-wrapper,
.guest-flow-wrapper #content {
  width: 100% !important;
  max-width: none !important;
  margin: 0 !important;
  padding: 0 !important;
}
</style>

<style lang="less">
@import "assets/styles/margaritaville-design/margaritaville-design";
@import "assets/styles/ecruise-design/ecruise-design";

@import "assets/styles/inputs";
@import "assets/styles/btns";
@import "assets/styles/fonts";
@import "assets/styles/switch";

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

* {
  // font-family: "OS";
  font-family: "OS", sans-serif;
  font-style: normal;
}

div {
  box-sizing: border-box;
}

&::-webkit-scrollbar {
  height: 4px;
  width: 4px;
  background: gray;
  border-radius: 10px;
}

&::-webkit-scrollbar-thumb {
  background: lightgrey;
  border-radius: 10px;
}

html {
  scroll-behavior: smooth;
  // box-sizing: border-box;

  body {
    height: 100vh;
    width: 100vw;
    margin: 0;
    overflow-x: hidden;
    background-color: #ffffff;

    &.loading {
      overflow: hidden;
    }

    .main-loader {
      display: flex;
      justify-content: center;
      align-items: center;
      background: white;
      width: 100vw;
      height: 100vh;
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 99999999999;
    }

    &.unauth-ui {
      background-color: #8dc7e1;
      background-repeat: no-repeat;
      background-attachment: fixed;
      background-position: bottom;
      background-size: cover;

      #blast {
        .app-viewport,
        .app-canvas,
        .wrapper {
          height: 100%;
        }

        #content {
          width: 100vw;
          margin-left: 0;
          background-color: unset;
        }
      }
    }

    #sidebar {
      display: flex !important;
    }

    #blast {
      width: 100%;
      min-width: 0;
      font-family: "OS", sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-align: center;
      color: #2c3e50;

      .app-viewport {
        position: relative;
        width: 100%;
        height: 100vh;
        height: 100dvh;
        min-width: 0;
      }

      .app-canvas {
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        flex-wrap: wrap;
        width: 100%;
        height: 100%;
        min-width: 0;
        transform-origin: top left;
      }

      .wrapper {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        flex-wrap: nowrap;
        width: 100%;
        height: calc(100vh - 84px);
        min-width: 0;

        #content {
          width: calc(100vw - 112px);
          height: 100%;
          min-width: 0;
          margin-left: 112px;
          border-top-left-radius: 15px;
          border-bottom-left-radius: 15px;
        }
      }
    }
  }
}

@media (max-width: 1200px) {
  // html {
  //   body {
  //     #blast {
  //       .wrapper {
  //         height: calc(100vh - 70px);
  //       }
  //     }
  //   }
  // }
}

@media (max-width: 850px) {}

html body:not(.unauth-ui) #blast .app-canvas .wrapper {
  height: calc(100vh - 84px);
  height: calc(100dvh - 84px);
}

html.tablet-layout-active,
body.tablet-layout-active {
  width: 100%;
  height: 100%;
  overflow: hidden !important;
  overscroll-behavior: none;
}

body.tablet-layout-active {
  #blast {
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
  }

  .app-viewport--scaled {
    position: fixed;
    inset: 0;
    // A transformed canvas is larger before scaling. Hidden overflow still lets
    // focus scroll its parent and move the header offscreen; clip forbids that.
    overflow: clip;

    .app-canvas {
      position: absolute;
      top: 0;
      left: 0;
      overflow: hidden;
    }

    #nav {
      box-sizing: border-box !important;
      width: 100% !important;
      height: 84px !important;
      min-width: 0;
      padding: 10px !important;
      gap: 10px !important;
      overflow: hidden;

      .content {
        min-width: 0;
      }

      .left-content {
        flex: 1 1 auto !important;
        overflow: hidden;
      }

      .center-content {
        flex: 0 0 280px !important;
        min-width: 0 !important;
        padding: 0 !important;
      }

      .right-content {
        flex: 0 0 260px !important;
        min-width: 0;
        justify-content: flex-end;
        overflow: hidden;
      }

      .left-content > img:first-child {
        max-width: 240px;
        height: auto;
      }

      .margaritaville-main-logo {
        max-width: 220px;
        height: auto;
      }

      .refresh-btn {
        flex: 0 0 96px;
        width: 96px !important;
        min-width: 96px !important;
        height: 52px !important;
        margin-left: 10px !important;
        padding: 0 !important;

        span {
          margin-left: 5px;
          font-size: 13px;
        }
      }

      .day-status {
        flex: 0 0 auto;
        margin-left: 10px;
        gap: 4px;

        img {
          width: 24px;
        }

        p {
          font-size: 13px;
          line-height: 17px;
          white-space: nowrap;
        }
      }

      .margaritaville-department-name {
        max-width: 120px;
        margin-left: 10px;
        overflow: hidden;
        font-size: 18px;
        line-height: 24px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .active-tax-name {
        padding: 0 6px;
        gap: 5px;
        font-size: 13px;
        line-height: 17px;
      }

      .pos-title {
        flex: 1 1 auto;
        max-width: 130px;
        min-width: 0;
        margin-right: 10px;
        overflow: hidden;
        font-size: 13px;
        line-height: 17px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .right-content .def-btn {
        flex: 0 0 110px;
        width: 110px !important;
        height: 52px !important;
        padding: 0 10px !important;
      }
    }

    .wrapper {
      width: 100% !important;
      height: calc(100% - 84px) !important;
      min-width: 0;
      min-height: 0;
      overflow: hidden;

      #content {
        width: calc(100% - 112px) !important;
        height: 100%;
        min-width: 0;
        min-height: 0;
        overflow-x: hidden;
        overflow-y: auto;
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;
      }
    }

    #sidebar {
      top: 84px !important;
      height: calc(100% - 108px) !important;
      overflow-x: hidden !important;
      overflow-y: hidden !important;
      flex-wrap: nowrap !important;

      .menu {
        width: 100%;
        height: 100%;
        min-height: 0;
        justify-content: flex-start;
        align-items: center;
        flex-direction: column;
        flex-wrap: nowrap;

        a {
          flex: 0 0 auto;

          &:last-child {
            margin-top: auto !important;
          }
        }
      }
    }

    #pos-root {
      width: 100%;
      height: 100%;
      min-width: 0;
      min-height: 0;
      overflow: hidden;

      #orders-list {
        width: 15% !important;
        min-width: 0 !important;
      }

      #menu {
        display: flex;
        flex-direction: column;
        width: 45% !important;
        min-width: 0 !important;

        .controls-wrapper,
        .search {
          flex: 0 0 auto;
        }

        .all-menu {
          display: flex;
          flex: 1 1 auto;
          min-height: 0;
          flex-direction: column;

          .list {
            flex: 1 1 auto;
            min-height: 0;
            height: auto;
          }
        }
      }

      #selected-order {
        width: 40% !important;
        min-width: 0 !important;
        overflow: hidden;
      }

      #orders-list .controls-wrapper {
        gap: 4px;

        .fn_btn {
          flex: 1 1 auto;
          width: auto;
          min-width: 0;
          padding-right: 6px;
          padding-left: 6px;

          span {
            font-size: 12px;
            white-space: nowrap;
          }
        }

        .refresh-btn {
          flex: 0 0 48px;
          width: 48px;
          min-width: 48px;
        }
      }
    }

    #dashboard-root,
    #printers-root {
      height: 100% !important;
      min-height: 0;
    }

    #reports-root .period.bds {
      height: auto !important;
      max-height: 320px;
    }
  }

  #keyboard.external {
    left: 0 !important;
    right: auto !important;
    width: 100vw !important;
    max-width: 100vw !important;
    transform: none !important;
  }
}
</style>

<style>

:root {--pos-header-height:84px;--pos-sidebar-width:112px}
body:not(.unauth-ui):not(.tablet-layout-active) #blast {height:100vh;height:100dvh;overflow:hidden}
body:not(.unauth-ui):not(.tablet-layout-active) #blast .app-canvas > .wrapper {height:calc(100% - var(--pos-header-height));width:100%;min-height:0}
body:not(.unauth-ui):not(.tablet-layout-active) #blast .app-canvas > .wrapper > #content {height:100%;width:calc(100% - var(--pos-sidebar-width));margin-left:var(--pos-sidebar-width);min-width:0;min-height:0;overflow:auto}
body:not(.unauth-ui):not(.tablet-layout-active) #sidebar {top:var(--pos-header-height);bottom:0;height:auto;box-sizing:border-box;overflow-y:auto;overflow-x:hidden;flex-wrap:nowrap;justify-content:flex-start}
body:not(.unauth-ui):not(.tablet-layout-active) #sidebar .menu {flex-shrink:0;flex-wrap:nowrap;flex-direction:column}
@media(max-width:1200px){:root{--pos-header-height:80px}}
@media(max-width:1100px){
 :root{--pos-sidebar-width:80px}
 body:not(.unauth-ui):not(.tablet-layout-active) #sidebar{width:72px}
 body:not(.unauth-ui):not(.tablet-layout-active) #sidebar .menu a,body:not(.unauth-ui):not(.tablet-layout-active) #sidebar .menu a.router-link-active{width:68px;height:60px;margin-bottom:8px}
 body:not(.unauth-ui):not(.tablet-layout-active) #sidebar .menu:first-child a:last-child{margin-top:12px}
 body:not(.unauth-ui):not(.tablet-layout-active) #sidebar .menu a .text{font-size:12px}
}


body:not(.unauth-ui) #blast .app-canvas > .guest-flow-wrapper {height:100dvh}
body:not(.unauth-ui) #blast .app-canvas > .guest-flow-wrapper > #content {width:100%;margin-left:0}
</style>

<style>
/* Use canvas coordinates once. Legacy viewport widths and nested percentages
   otherwise shrink the header and each POS column a second time on tablets. */
html body.tablet-layout-active #blast .app-canvas #nav{width:100%!important;max-width:100%!important}
html body.tablet-layout-active #blast .app-canvas #pos-root #orders-list,
html body.tablet-layout-active #blast .app-canvas #pos-root #menu,
html body.tablet-layout-active #blast .app-canvas #pos-root #selected-order{width:100%!important}
/* Payment is teleported to body: give it the same coordinate system as POS. */
body.tablet-layout-active .payment-workspace:not(.type-choice){
  top:calc(84px * var(--terminal-scale))!important;
  left:calc(96px * var(--terminal-scale))!important;
  right:auto!important;bottom:auto!important;
  width:calc(var(--terminal-canvas-width) - 96px);
  height:calc(var(--terminal-canvas-height) - 84px);
  transform:scale(var(--terminal-scale));transform-origin:top left;
}
</style>
